import * as React from 'react';
import { CommandBarButton, PrimaryButton, SearchBox } from '@fluentui/react';
import useDebouncedState from 'hooks/useDebouncedState';
import { ArtikelDetail, ArtikelDetailFilter, useArtikelDetailList } from 'api/artikelDetail';
import {
    ArtikelMaschinentype,
    ArtikelMaschinentypeFilter,
    useArtikelMaschinentypeList,
} from 'api/artikelMaschinentype';
import { getFocusStyle, getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import classnames from 'classnames';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List } from '@fluentui/react/lib/List';
import { ArtikelGruppeByNr } from 'formComponents/lookups/ArtikelGruppeByNr';
import { getArtikelDetailId } from 'utils/helper';
import {
    ArtikelEigenschaftLink,
    ArtikelEigenschaftLinkFilter,
    useArtikelEigenschaftLinkList,
} from 'api/artikelEigenschaftLink';
import usePersistentState from 'hooks/usePersistentState';
import { formStyles } from 'styles/formStyles';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { SavingState } from 'api/savingState';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { ArtikelDetailItem } from './ArtikelDetailtem';
import { Maschinentype } from '../../../formComponents/lookups/Maschinentype';

const defaultMaxRows = 250;

const theme: ITheme = getTheme();
const { palette, fonts } = theme;

const styles = mergeStyleSets({
    textField: {
        cursor: 'pointer !important',
        textDecoration: 'underline',
    },
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content max-content max-content 1fr',
        height: '100%',
    },
    containerInsideDialog: {
        height: '80vh !important',
    },
    itemCell: [
        getFocusStyle(theme, { inset: -1 }),
        {
            minHeight: 54,
            boxSizing: 'border-box',
            display: 'flex',
        },
    ],
    clickable: [
        getFocusStyle(theme, { inset: -1 }),
        {
            cursor: 'pointer',
            selectors: {
                '&:hover': { background: palette.neutralLight },
            },
        },
    ],
    active: {
        backgroundColor: '#CCC',
    },
    itemContent: {
        marginLeft: 10,
        overflow: 'hidden',
        flexGrow: 1,
    },
    itemName: [
        fonts.large,
        {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    ],
    itemIndex: {
        fontSize: fonts.small.fontSize,
        color: palette.neutralTertiary,
        marginBottom: 10,
    },
    chevron: {
        alignSelf: 'center',
        marginLeft: 10,
        color: palette.neutralTertiary,
        fontSize: fonts.large.fontSize,
        flexShrink: 0,
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '250px 250px max-content max-content max-content 1fr max-content max-content',
        gridColumnGap: 10,
    },
    searchLine2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridColumnGap: 10,
    },
    searchTextFromBeginning: {
        marginTop: 6,
    },
    searchTextMultiPart: {
        marginTop: 6,
    },
    infoLine: {
        display: 'flex',
    },
    reset: {
        right: 10,
    },
});

interface ArtikelDetailSearchProps {
    parentView: string;
    insideDialog?: boolean;
    selectedArtikelGruppeNr?: number;
    selectedArtikelNr?: string;
    onNew?: () => void;
    onEdit?: (artikelDetail: ArtikelDetail) => void;
    onClick?: (artikelDetail: ArtikelDetail) => void;
}

interface FilterValues {
    maxRows: number;
    artikelGruppeNr: number | null;
    searchText: string;
    searchTextFromBeginning: boolean;
    searchTextMultiPart: boolean;
    einheit?: string;
    maschinentypeNr?: number;
    staerkeVon?: number;
    staerkeBis?: number;
    breiteVon?: number;
    breiteBis?: number;
    halbeBreite?: boolean;
    laufmeterVon?: number;
    laufmeterBis?: number;
    quadrmeterVon?: number;
    quadrmeterBis?: number;
    minVerfuegbar?: number;
    includeArchiv: boolean;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    artikelGruppeNr: null,
    searchText: '',
    searchTextFromBeginning: false,
    searchTextMultiPart: true,
    includeArchiv: false,
};

export interface ArtikelDetailSearchHandle {
    refetch: () => void;
}

const FILTER_VALUES_STORAGE_KEY = 'salesmaster.ArtikelDetailSearch.filterValues';

export const ArtikelDetailSearch = React.forwardRef(
    (
        {
            parentView,
            insideDialog = false,
            selectedArtikelGruppeNr,
            selectedArtikelNr,
            onNew,
            onEdit,
            onClick,
        }: ArtikelDetailSearchProps,
        ref,
    ) => {
        // const isSmallScreen = useMediaQuery({ query: `(max-width: 1450px)` });

        const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>(
            'long',
            parentView + '.ArtikelDetailSearch.viewMode',
        );

        const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
            const storedValue = sessionStorage.getItem(FILTER_VALUES_STORAGE_KEY);
            if (storedValue) {
                return {
                    ...defaultFilterValues,
                    ...JSON.parse(storedValue),
                };
            }

            return {
                ...defaultFilterValues,
            };
        });

        React.useEffect(() => {
            sessionStorage.setItem(FILTER_VALUES_STORAGE_KEY, JSON.stringify(debouncedFilterValues));
        }, [debouncedFilterValues]);

        const filter = React.useMemo<ArtikelDetailFilter>(
            () => ({
                artikelGruppeNr: debouncedFilterValues.artikelGruppeNr || undefined,
                searchText: debouncedFilterValues.searchText,
                searchTextFromBeginning: debouncedFilterValues.searchTextFromBeginning,
                searchTextMultiPart: debouncedFilterValues.searchTextMultiPart,
                einheit: debouncedFilterValues.einheit,
                maschinentypeNr: debouncedFilterValues.maschinentypeNr,
                staerkeVon: debouncedFilterValues.staerkeVon,
                staerkeBis: debouncedFilterValues.staerkeBis,
                breiteVon: debouncedFilterValues.breiteVon,
                breiteBis: debouncedFilterValues.breiteBis,
                halbeBreite: debouncedFilterValues.halbeBreite,
                laufmeterVon: debouncedFilterValues.laufmeterVon,
                laufmeterBis: debouncedFilterValues.laufmeterBis,
                quadrmeterVon: debouncedFilterValues.quadrmeterVon,
                quadrmeterBis: debouncedFilterValues.quadrmeterBis,
                minVerfuegbar: debouncedFilterValues.minVerfuegbar,
                includeArchiv: debouncedFilterValues.includeArchiv,
                maxRows: debouncedFilterValues.maxRows,
                withHauptlieferant: true,
            }),
            [debouncedFilterValues],
        );

        const artikelDetailList = useArtikelDetailList(filter, { enabled: false });

        React.useEffect(() => {
            artikelDetailList.refetch();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [filter, viewMode]);

        React.useImperativeHandle(ref, () => ({
            refetch: () => {
                artikelDetailList.refetch();
            },
        }));

        const artikelEigenschaftLinkFilter = React.useMemo<ArtikelEigenschaftLinkFilter>(() => {
            if (artikelDetailList.isLoading || artikelDetailList.isLoading || artikelDetailList.isError) {
                return {};
            }
            return {
                artikelNrList: artikelDetailList.data?.map((artikelDetail) => artikelDetail.artikelNr) || [],
            };
        }, [artikelDetailList]);

        const artikelMaschinentypeFilter = React.useMemo<ArtikelMaschinentypeFilter>(() => {
            if (artikelDetailList.isLoading || artikelDetailList.isLoading || artikelDetailList.isError) {
                return {};
            }
            return {
                artikelNrList: artikelDetailList.data?.map((artikelDetail) => artikelDetail.artikelNr) || [],
            };
        }, [artikelDetailList]);

        const artikelEigenschaftLinkList = useArtikelEigenschaftLinkList(artikelEigenschaftLinkFilter, {
            enabled: false,
        });

        const artikelMaschinentypeList = useArtikelMaschinentypeList(artikelMaschinentypeFilter, {
            enabled: false,
        });

        React.useEffect(() => {
            if (artikelEigenschaftLinkFilter.artikelNrList) {
                artikelEigenschaftLinkList.refetch().then();
                artikelMaschinentypeList.refetch().then();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [artikelEigenschaftLinkFilter]);

        const artikelEigenschaftLinkListByANr = React.useMemo<{ [key: string]: ArtikelEigenschaftLink[] }>(() => {
            if (
                artikelDetailList.isLoading ||
                artikelDetailList.isError ||
                artikelEigenschaftLinkList.isLoading ||
                artikelEigenschaftLinkList.isError ||
                (artikelEigenschaftLinkList.data?.length || 0) === 0
            ) {
                return {};
            }

            const newArtikelEigenschaftLinkListByArtikelNr: { [key: string]: ArtikelEigenschaftLink[] } = {};

            artikelEigenschaftLinkList.data?.forEach((artikelEigenschaftenLink) => {
                if (!newArtikelEigenschaftLinkListByArtikelNr[artikelEigenschaftenLink.artikelNr]) {
                    newArtikelEigenschaftLinkListByArtikelNr[artikelEigenschaftenLink.artikelNr] = [];
                }

                newArtikelEigenschaftLinkListByArtikelNr[artikelEigenschaftenLink.artikelNr].push(
                    artikelEigenschaftenLink,
                );
            });

            return newArtikelEigenschaftLinkListByArtikelNr;
        }, [artikelDetailList, artikelEigenschaftLinkList]);

        const artikelMaschinentypeListByANr = React.useMemo<{ [key: string]: ArtikelMaschinentype[] }>(() => {
            if (
                artikelDetailList.isLoading ||
                artikelDetailList.isError ||
                artikelMaschinentypeList.isLoading ||
                artikelMaschinentypeList.isError ||
                (artikelMaschinentypeList.data?.length || 0) === 0
            ) {
                return {};
            }

            const newArtikelMaschinentypeListByArtikelNr: { [key: string]: ArtikelMaschinentype[] } = {};

            artikelMaschinentypeList.data?.forEach((artikelMaschinentype) => {
                if (!newArtikelMaschinentypeListByArtikelNr[artikelMaschinentype.artikelNr]) {
                    newArtikelMaschinentypeListByArtikelNr[artikelMaschinentype.artikelNr] = [];
                }

                newArtikelMaschinentypeListByArtikelNr[artikelMaschinentype.artikelNr].push(artikelMaschinentype);
            });

            return newArtikelMaschinentypeListByArtikelNr;
        }, [
            artikelDetailList.isError,
            artikelDetailList.isLoading,
            artikelMaschinentypeList.data,
            artikelMaschinentypeList.isError,
            artikelMaschinentypeList.isLoading,
        ]);

        const listRef = React.useRef<List<ArtikelDetail>>(null);

        React.useEffect(() => {
            if (artikelMaschinentypeListByANr) {
                listRef.current?.forceUpdate();
            }
        }, [artikelMaschinentypeListByANr]);

        const reset = () => {
            setFilterValues(defaultFilterValues);
        };

        const onRenderCell = React.useCallback(
            (artikelDetail: ArtikelDetail | undefined): JSX.Element => {
                if (!artikelDetail) {
                    return <div />;
                }

                const id = getArtikelDetailId(artikelDetail.artikelGruppeNr, artikelDetail.artikelNr);

                return (
                    <div
                        key={`${artikelDetail.artikelGruppeNr}|${artikelDetail.artikelNr}`}
                        className={classnames(styles.itemCell, {
                            [styles.clickable]: !!onClick,
                            [formStyles.listItemSelected]:
                                selectedArtikelGruppeNr === artikelDetail.artikelGruppeNr &&
                                selectedArtikelNr === artikelDetail.artikelNr,
                        })}
                        data-is-focusable
                        onClick={() => {
                            if (onClick) {
                                onClick(artikelDetail);
                            }
                        }}
                    >
                        <ArtikelDetailItem
                            key={id}
                            artikelDetail={artikelDetail}
                            viewMode={viewMode}
                            artikelEigenschaftLinkList={artikelEigenschaftLinkListByANr[artikelDetail.artikelNr] || []}
                            artikelMachinentypeList={artikelMaschinentypeListByANr[artikelDetail.artikelNr] || []}
                            editArtikel={onEdit}
                        />
                    </div>
                );
            },
            [
                onClick,
                selectedArtikelGruppeNr,
                selectedArtikelNr,
                viewMode,
                artikelEigenschaftLinkListByANr,
                artikelMaschinentypeListByANr,
                onEdit,
            ],
        );

        const renderEinheit = () => (
            <EinheitByCode
                label="Einheit"
                value={filterValues.einheit || ''}
                disabled={false}
                menuPlacement="bottom"
                allowEmpty
                onChange={(newEinheit) => {
                    setFilterValues({
                        ...filterValues,
                        einheit: newEinheit,
                    });
                }}
            />
        );

        const renderMaschinentype = () => (
            <Maschinentype
                label="Maschinentype"
                value={filterValues.maschinentypeNr || 0}
                disabled={false}
                menuPlacement="bottom"
                allowEmpty
                onChange={(newMaschinentypeNr) => {
                    setFilterValues({
                        ...filterValues,
                        maschinentypeNr: newMaschinentypeNr,
                    });
                }}
            />
        );

        const renderCheckbox = (column: keyof FilterValues, label: string) => (
            <Checkbox
                className={formStyles.checkboxInsideFormAligned}
                label={label}
                checked={filterValues?.[column] as boolean}
                onChange={(event, checked) => {
                    setFilterValues({
                        ...filterValues,
                        [column]: checked || false,
                    });
                }}
            />
        );

        const renderDecimalField = (
            column: keyof FilterValues,
            label: string,
            options: FormDecimalFieldOptions = {},
        ) => (
            <FormDecimalField
                value={(filterValues?.[column] || '') as number}
                savingState={SavingState.notChanged}
                options={{ ...options }}
                onChange={(newValue): void => {
                    setFilterValues({
                        ...filterValues,
                        [column]: newValue,
                    });
                }}
                label={label}
            />
        );

        return (
            <div
                className={classnames(styles.container, {
                    [styles.containerInsideDialog]: insideDialog,
                })}
            >
                <div className={styles.searchLine1}>
                    <ArtikelGruppeByNr
                        value={filterValues.artikelGruppeNr}
                        disabled={false}
                        label="Artikelgruppe"
                        includeArchiv={filterValues.includeArchiv}
                        allowEmpty
                        renderLabel="placeholder"
                        onChange={(newValue): void => {
                            setFilterValues({
                                ...filterValues,
                                artikelGruppeNr: newValue,
                            });
                        }}
                    />

                    <SearchBox
                        value={filterValues.searchText}
                        onChange={(event, newValue): void => {
                            setFilterValues({
                                ...filterValues,
                                searchText: newValue === undefined ? '' : newValue,
                            });
                        }}
                        placeholder="Artikel-Nr. / Text"
                        autoFocus
                    />

                    <Checkbox
                        className={styles.searchTextFromBeginning}
                        label="Anfang"
                        checked={filterValues.searchTextFromBeginning}
                        inputProps={{
                            style: {
                                marginTop: -5,
                                fontSize: 10,
                            },
                        }}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                searchTextFromBeginning: checked || false,
                                searchTextMultiPart: checked ? !checked : filterValues.searchTextMultiPart,
                            });
                        }}
                    />

                    <Checkbox
                        className={styles.searchTextMultiPart}
                        label="Jedes Wort extra"
                        checked={filterValues.searchTextMultiPart}
                        inputProps={{
                            style: {
                                marginTop: -5,
                                fontSize: 10,
                            },
                        }}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                searchTextFromBeginning: checked ? !checked : filterValues.searchTextFromBeginning,
                                searchTextMultiPart: checked || false,
                            });
                        }}
                    />

                    <div>
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Archivierte Artikel anzeigen"
                            checked={filterValues.includeArchiv}
                            onChange={(event, checked) => {
                                setFilterValues({
                                    ...filterValues,
                                    includeArchiv: checked || false,
                                });
                            }}
                        />
                    </div>

                    <div />
                    <div>
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Kurzansicht"
                            checked={viewMode === 'short'}
                            onChange={(event, checked) => {
                                setViewMode(checked ? 'short' : 'long');
                            }}
                        />
                    </div>
                    {onNew ? <PrimaryButton onClick={onNew} text="Neuer Artikel" /> : <div />}
                </div>
                <div className={styles.searchLine2}>
                    <div>
                        <div>{renderDecimalField('staerkeVon', 'Stärke von (mm)', { fixed: 3 })}</div>
                        <div>{renderDecimalField('staerkeBis', 'Stärke bis (mm)', { fixed: 3 })}</div>
                    </div>
                    <div>
                        <div>{renderDecimalField('laufmeterVon', 'Länge von (m)', { fixed: 3 })}</div>
                        <div>{renderDecimalField('laufmeterBis', 'Länge bis (m)', { fixed: 3 })}</div>
                    </div>
                    <div>
                        <div>{renderDecimalField('quadrmeterVon', 'Fläche von (m²)', { fixed: 3 })}</div>
                        <div>{renderDecimalField('quadrmeterBis', 'Fläche bis (m²)', { fixed: 3 })}</div>
                    </div>
                    <div>
                        <div>{renderDecimalField('breiteVon', 'Breite von (m)', { fixed: 3 })}</div>
                        <div>{renderDecimalField('breiteBis', 'Breite bis (m)', { fixed: 3 })}</div>
                    </div>
                    <div>
                        <div>{renderEinheit()}</div>
                        <div>{renderMaschinentype()}</div>
                    </div>
                    <div>
                        <div>{renderDecimalField('minVerfuegbar', 'Mind. Verfügbar', { fixed: 3 })}</div>
                        <div>{renderCheckbox('halbeBreite', 'Halbschlauch')}</div>
                    </div>
                </div>

                <div className={styles.reset}>
                    <CommandBarButton text="Zurücksetzen" onClick={reset} />
                </div>
                <div>
                    <AutoSizer>
                        {({ height, width }) => (
                            <div
                                style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}
                                onScroll={() => {
                                    // update doesn't trigger automatically inside a dialog (probably a bug?)
                                    listRef.current?.forceUpdate();
                                }}
                            >
                                <List
                                    key={viewMode}
                                    ref={listRef}
                                    items={artikelDetailList.data || []}
                                    onRenderCell={onRenderCell}
                                    {...{ dependencies1: artikelMaschinentypeListByANr }}
                                />
                            </div>
                        )}
                    </AutoSizer>
                </div>
            </div>
        );
    },
);
